import { useEffect, useRef, useState } from "react";
import { useBlocker, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CUSTOM_PRESET,
  LABEL_PRESETS,
  normalizeLabelSize,
  settingsUpdateSchema,
  type SettingsDto,
} from "@hisense/shared";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import {
  useSettings,
  useUpdateSettings,
  useDocumentCounters,
} from "@/api/hooks";
import { PageBody } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { LoadingState, QueryErrorState } from "@/components/ui/States";
import { OptionListEditor } from "@/components/settings/OptionListEditor";
import { PaymentCompanies } from "@/components/settings/PaymentCompanies";
import { SystemInformation } from "@/components/releases/SystemInformation";
import { ConfigurationHistory } from "@/components/settings/ConfigurationHistory";
import { CurrencyRatesPanel } from "@/components/settings/CurrencyRatesPanel";
import {
  sectionDirty,
  sectionPayload,
  replaceSection,
  isZhLabelList,
  formatCounter,
  type SettingsSection,
} from "@/components/settings/settingsDraft";
import { LabelPreview } from "@/components/products/LabelPreview";
import { printLabel } from "@/lib/labelPrinter";
import { ApiClientError, getLocalizedError } from "@/lib/api";
const dictionaries = [
  "productTypes",
  "productUnits",
  "labTestStandards",
  "supplierSpecializations",
  "paymentTerms",
  "currencies",
  "shippingMethods",
  "poPriorities",
  "quotationPreparedBy",
  "weightUnits",
  "shippingMarks",
] as const;
const tabs = ["catalog", "company", "output", "system"] as const;
export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const canEdit = !!user && can("settings:manage");
  const query = useSettings();
  const save = useUpdateSettings();
  const counters = useDocumentCounters(canEdit);
  const [params, setParams] = useSearchParams();
  const tab = tabs.find((x) => x === params.get("tab")) ?? "catalog";
  const [base, setBase] = useState<SettingsDto | null>(null);
  const [draft, setDraft] = useState<SettingsDto | null>(null);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState<
    Record<string, { text: string; error: boolean }>
  >({});
  const [busy, setBusy] = useState<SettingsSection | null>(null);
  const [companyDirty, setCompanyDirty] = useState(false);
  const sections: SettingsSection[] = [
    "company",
    "defaults",
    "fx",
    "output",
    ...dictionaries.map((k) => `list:${k}` as const),
  ];
  const dirty = !!(
    draft &&
    base &&
    sections.some((s) => sectionDirty(draft, base, s))
  );
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    if (query.data && !dirtyRef.current) {
      setBase(query.data);
      setDraft(structuredClone(query.data));
    }
  }, [query.data]);
  useEffect(() => {
    if (!dirty && !companyDirty) return;
    const stop = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", stop);
    return () => window.removeEventListener("beforeunload", stop);
  }, [dirty, companyDirty]);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      (dirty || companyDirty || !!busy) &&
      currentLocation.pathname !== nextLocation.pathname,
  );
  if (query.isLoading || !draft || !base) {
    if (query.error)
      return (
        <PageBody>
          <QueryErrorState
            error={query.error}
            message={t("settings.errorLoad")}
            forbiddenMessage={t("errors.forbidden")}
            onRetry={() => query.refetch()}
          />
        </PageBody>
      );
    return (
      <PageBody>
        <LoadingState />
      </PageBody>
    );
  }
  const setNote = (s: string, text: string, error = false) =>
    setMessage((m) => ({ ...m, [s]: { text, error } }));
  const submit = async (section: SettingsSection) => {
    if (busy || !canEdit) return;
    const payload = {
      ...sectionPayload(draft, section),
      expectedRevision: base.revision ?? 0,
    };
    const parsed = settingsUpdateSchema.safeParse(payload);
    if (!parsed.success) {
      const labels: Record<string, string> = {
        name: "settings.companyName",
        address: "common.address",
        phone: "common.phone",
        email: "common.email",
        taxId: "common.taxId",
        defaultCurrency: "settings.defaultCurrency",
        defaultProductUnit: "settings.defaultProductUnit",
        widthMm: "settings.widthMm",
        heightMm: "settings.heightMm",
        presetKey: "settings.labelPreset",
        // Khung tiền tệ theo công ty & tỷ giá (v1.8).
        rate: "fxSettings.ratesTitle",
        vietnam: "fxSettings.default_vietnam",
        china: "fxSettings.default_china",
      };
      const fields = [
        ...new Set(
          parsed.error.issues
            .map((issue) => {
              const field = issue.path
                .filter((x) => typeof x === "string")
                .at(-1) as string;
              // Trường chưa có nhãn thì bỏ, không in tên khoá kỹ thuật ("settings.rate") cho người dùng.
              return t(labels[field] ?? `settings.${field}`, { defaultValue: "" });
            })
            .filter(Boolean),
        ),
      ];
      // Câu hướng dẫn đúng khung đang lưu: trước 12/10/2026 mọi khung dùng chung một câu nhắc kích thước tem, kể cả khung tỷ giá.
      const hint =
        section === "fx"
          ? t("fxSettings.invalid")
          : section === "output"
            ? t("settingsWorkspace.invalid")
            : t("settingsWorkspace.invalidGeneric");
      setNote(section, fields.length ? `${fields.join(", ")}: ${hint}` : hint, true);
      return;
    }
    setBusy(section);
    setNote(section, "");
    try {
      const result = await save.mutateAsync(parsed.data);
      setBase(result.data);
      setDraft((d) =>
        d ? replaceSection(d, result.data, section) : result.data,
      );
      setNote(section, t("common.saved"));
    } catch (error) {
      let text = getLocalizedError(error, t, "settings.errorSave");
      if (error instanceof ApiClientError && error.fields?.value)
        text += ` ${error.fields.value}`;
      setNote(section, text, true);
      if (error instanceof ApiClientError && error.code === "settings_changed")
        void query.refetch();
    } finally {
      setBusy(null);
    }
  };
  const footer = (section: SettingsSection) => (
    <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface p-3">
      {message[section]?.text && (
        <p
          role={message[section].error ? "alert" : "status"}
          className={`mr-auto text-xs ${message[section].error ? "text-danger" : "text-ok"}`}
        >
          {message[section].text}
        </p>
      )}
      {sectionDirty(draft, base, section) && (
        <span className="mr-auto text-xs text-warn">
          {t("settingsWorkspace.unsaved")}
        </span>
      )}
      {canEdit && (
        <>
          <Button
            size="sm"
            variant="ghost"
            disabled={!!busy || !sectionDirty(draft, base, section)}
            onClick={() => {
              setDraft((d) => (d ? replaceSection(d, base, section) : d));
              setNote(section, "");
            }}
          >
            {t("settingsWorkspace.discard")}
          </Button>
          <Button
            size="sm"
            type="submit"
            disabled={!!busy || !sectionDirty(draft, base, section)}
          >
            {busy === section ? t("common.saving") : t("common.save")}
          </Button>
        </>
      )}
    </div>
  );
  const companyField = (
    key: keyof SettingsDto["companyInfo"],
    label: string,
  ) => (
    <div key={key}>
      <FieldLabel htmlFor={`settings-company-${key}`}>{label}</FieldLabel>
      <Input
        id={`settings-company-${key}`}
        type={key === "email" ? "email" : "text"}
        required={key === "name"}
        value={draft.companyInfo[key]}
        disabled={!canEdit || !!busy}
        onChange={(e) =>
          setDraft({
            ...draft,
            companyInfo: { ...draft.companyInfo, [key]: e.target.value },
          })
        }
      />
    </div>
  );
  const labelSize = normalizeLabelSize(draft.labelPrinter);
  const testLabel = {
    productRef: "TEST-001",
    description: t("settingsWorkspace.testDescription"),
    barcode: "TEST001",
    count: 1,
  };
  return (
    <PageBody className="min-h-0 overflow-y-auto">
      <div
        role="tablist"
        aria-label={t("settings.sectionsLabel")}
        className="flex shrink-0 gap-2 overflow-x-auto rounded-md border border-line bg-sunken p-2"
      >
        {tabs.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`settings-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`settings-${id}`}
            tabIndex={tab === id ? 0 : -1}
            onKeyDown={(event) => {
              const index = tabs.indexOf(id);
              const nextIndex =
                event.key === "ArrowRight"
                  ? (index + 1) % tabs.length
                  : event.key === "ArrowLeft"
                    ? (index + tabs.length - 1) % tabs.length
                    : event.key === "Home"
                      ? 0
                      : event.key === "End"
                        ? tabs.length - 1
                        : null;
              if (nextIndex === null) return;
              event.preventDefault();
              if (companyDirty) {
                setNote("page", t("settingsWorkspace.finishCompany"), true);
                return;
              }
              const next = tabs[nextIndex]!;
              setParams({ tab: next });
              document.getElementById(`settings-tab-${next}`)?.focus();
            }}
            className={`rounded-sm px-4 py-2 text-sm ${tab === id ? "bg-surface font-semibold text-brand" : "text-ink-soft"}`}
            onClick={() => {
              if (companyDirty && id !== tab) {
                setNote("page", t("settingsWorkspace.finishCompany"), true);
                return;
              }
              setParams({ tab: id });
            }}
          >
            {id === "system" ? t("releases.system") : t(`settings.${id}Tab`)}
          </button>
        ))}
      </div>
      {message.page?.text && (
        <p role="alert" className="text-sm text-danger">
          {message.page.text}
        </p>
      )}
      {(query.data?.revision ?? 0) !== (base.revision ?? 0) && (
        <div
          role="alert"
          className="rounded-md border border-warn bg-warn-tint p-3 text-sm"
        >
          {t("settingsWorkspace.changed")}{" "}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              if (query.data) {
                setBase(query.data);
                setDraft(structuredClone(query.data));
                setMessage({});
              }
            }}
          >
            {t("settingsWorkspace.reloadDiscard")}
          </Button>
        </div>
      )}
      <div
        role="tabpanel"
        id={`settings-${tab}`}
        aria-labelledby={`settings-tab-${tab}`}
        className="mt-3 space-y-4"
      >
        {tab === "system" && <SystemInformation />}
        {tab === "catalog" && (
          <>
            <div>
              <FieldLabel htmlFor="settings-search">
                {t("settingsWorkspace.search")}
              </FieldLabel>
              <Input
                id="settings-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("settingsWorkspace.searchHint")}
              />
            </div>
            {dictionaries
              .filter((key) =>
                `${t(`settings.${key}`)} ${(draft.businessOperations.optionLists[key] ?? []).join(" ")} ${Object.values(draft.businessOperations.optionLabelsZh?.[key] ?? {}).join(" ")}`
                  .toLocaleLowerCase()
                  .includes(search.toLocaleLowerCase()),
              )
              .map((key) => (
                <details
                  key={key}
                  open={search.length > 0 || undefined}
                  className="rounded-md border border-line bg-surface"
                >
                  <summary className="cursor-pointer px-4 py-3 text-sm font-semibold">
                    {t(`settings.${key}`)}{" "}
                    <span className="ml-2 font-mono text-xs text-ink-faint">
                      {draft.businessOperations.optionLists[key]?.length ?? 0}
                    </span>
                    {sectionDirty(draft, base, `list:${key}`) && (
                      <span className="ml-2 text-xs text-warn">
                        {t("settingsWorkspace.unsaved")}
                      </span>
                    )}
                  </summary>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void submit(`list:${key}`);
                    }}
                  >
                    <OptionListEditor
                      title={t(`settings.${key}`)}
                      description={t(`settings.${key}Description`)}
                      placeholder={t("settingsWorkspace.newOption")}
                      values={draft.businessOperations.optionLists[key] ?? []}
                      disabled={!canEdit || !!busy}
                      allowEmpty={
                        ![
                          "productTypes",
                          "productUnits",
                          "currencies",
                        ].includes(key)
                      }
                      onChange={(values) =>
                        setDraft({
                          ...draft,
                          businessOperations: {
                            ...draft.businessOperations,
                            optionLists: {
                              ...draft.businessOperations.optionLists,
                              [key]: values,
                            },
                          },
                        })
                      }
                      {...(isZhLabelList(key)
                        ? {
                            labels: draft.businessOperations.optionLabelsZh?.[key] ?? {},
                            labelPlaceholder: t("settings.optionLabelZh"),
                            onLabelsChange: (labels: Record<string, string>) =>
                              setDraft({
                                ...draft,
                                businessOperations: {
                                  ...draft.businessOperations,
                                  optionLabelsZh: { ...draft.businessOperations.optionLabelsZh, [key]: labels },
                                },
                              }),
                          }
                        : {})}
                    />
                    {footer(`list:${key}`)}
                  </form>
                </details>
              ))}
            <form
              className="rounded-md border border-line bg-surface"
              onSubmit={(e) => {
                e.preventDefault();
                void submit("defaults");
              }}
            >
              <div className="p-4">
                <h3 className="text-sm font-semibold">
                  {t("settings.defaults")}
                </h3>
                <p className="mt-1 text-xs text-ink-soft">
                  {t("settings.defaultsDescription")}
                </p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {(["defaultCurrency", "defaultProductUnit"] as const).map(
                    (key) => (
                      <div key={key}>
                        <FieldLabel htmlFor={`settings-${key}`}>
                          {t(`settings.${key}`)}
                        </FieldLabel>
                        <Select
                          id={`settings-${key}`}
                          disabled={!canEdit || !!busy}
                          value={draft.system[key]}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              system: {
                                ...draft.system,
                                [key]: e.target.value,
                              },
                            })
                          }
                        >
                          {[
                            ...new Set([
                              ...(draft.businessOperations.optionLists[
                                key === "defaultCurrency"
                                  ? "currencies"
                                  : "productUnits"
                              ] ?? []),
                              draft.system[key],
                            ]),
                          ].map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                        </Select>
                      </div>
                    ),
                  )}
                </div>
              </div>
              {footer("defaults")}
            </form>
            <form
              className="rounded-md border border-line bg-surface"
              onSubmit={(e) => {
                e.preventDefault();
                void submit("fx");
              }}
            >
              <div className="border-b border-line p-4">
                <h3 className="text-sm font-semibold">{t("fxSettings.title")}</h3>
                <p className="mt-1 text-xs text-ink-soft">{t("fxSettings.description")}</p>
              </div>
              <CurrencyRatesPanel draft={draft} saved={query.data} onChange={setDraft} disabled={!canEdit || !!busy} />
              {footer("fx")}
            </form>
            {canEdit && (
              <details className="rounded-md border border-line bg-surface p-4">
                <summary className="cursor-pointer text-sm font-semibold">
                  {t("settings.counters")}
                </summary>
                <p className="my-2 text-xs text-ink-soft">
                  {t("settings.countersDescription")}
                </p>
                {counters.error ? (
                  <p role="alert">{t("settings.errorLoad")}</p>
                ) : (
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {counters.data?.map((c) => (
                      <li
                        key={c.key}
                        className="flex justify-between gap-3 text-xs"
                      >
                        <span>
                          {t(
                            `settingsWorkspace.counter_${c.key.split(":")[0]}`,
                          )}
                          {c.key.includes(":")
                            ? ` · ${c.key.split(":")[1]}`
                            : ""}
                        </span>
                        <span className="font-mono tabular">
                          {formatCounter(c)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </details>
            )}
          </>
        )}
        {tab === "company" && (
          <>
            <form
              className="rounded-md border border-line bg-surface"
              onSubmit={(e) => {
                e.preventDefault();
                void submit("company");
              }}
            >
              <h3 className="px-4 pt-4 text-sm font-semibold">
                {t("settings.companyInformation")}
              </h3>
              <div className="grid gap-3 p-4 sm:grid-cols-2">
                {companyField("name", t("settings.companyName"))}
                {companyField("address", t("common.address"))}
                {companyField("phone", t("common.phone"))}
                {companyField("email", t("common.email"))}
                {companyField("taxId", t("common.taxId"))}
              </div>
              {footer("company")}
            </form>
            <PaymentCompanies
              canEdit={canEdit}
              onDirtyChange={setCompanyDirty}
            />
          </>
        )}
        {tab === "output" && (
          <form
            className="rounded-md border border-line bg-surface"
            onSubmit={(e) => {
              e.preventDefault();
              void submit("output");
            }}
          >
            <div className="grid gap-5 p-4 lg:grid-cols-2">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">
                  {t("settings.labelPrinter")}
                </h3>
                <FieldLabel htmlFor="settings-preset">
                  {t("settings.labelPreset")}
                </FieldLabel>
                <Select
                  id="settings-preset"
                  value={draft.labelPrinter.presetKey}
                  disabled={!canEdit || !!busy}
                  onChange={(e) => {
                    const p = LABEL_PRESETS.find(
                      (x) => x.key === e.target.value,
                    );
                    setDraft({
                      ...draft,
                      labelPrinter: p
                        ? {
                            // Giữ cờ xoay khi đổi khổ: đó là quy ước chiều giấy
                            // của MÁY IN, không phải thuộc tính của khổ tem.
                            ...draft.labelPrinter,
                            presetKey: p.key,
                            widthMm: p.widthMm,
                            heightMm: p.heightMm,
                          }
                        : { ...draft.labelPrinter, presetKey: CUSTOM_PRESET },
                    });
                  }}
                >
                  {LABEL_PRESETS.map((p) => (
                    <option key={p.key} value={p.key}>
                      {t("settingsWorkspace.size", {
                        width: p.widthMm,
                        height: p.heightMm,
                      })}
                    </option>
                  ))}
                  <option value={CUSTOM_PRESET}>
                    {t("settings.labelCustom")}
                  </option>
                </Select>
                <div className="grid grid-cols-2 gap-3">
                  {(["widthMm", "heightMm"] as const).map((key) => (
                    <div key={key}>
                      <FieldLabel htmlFor={`settings-${key}`}>
                        {t(`settings.${key}`)}
                      </FieldLabel>
                      <Input
                        id={`settings-${key}`}
                        type="number"
                        min={20}
                        max={100}
                        step={1}
                        value={draft.labelPrinter[key] || ""}
                        disabled={
                          !canEdit ||
                          !!busy ||
                          draft.labelPrinter.presetKey !== CUSTOM_PRESET
                        }
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            labelPrinter: {
                              ...draft.labelPrinter,
                              [key]: Number(e.target.value),
                            },
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
                {/* Bản in ra nằm ngang / bị cắt là do driver khai chiều giấy
                    ngược với ta, và trình duyệt không sửa được chuyện đó. Ô
                    này để người dùng lật cho khớp; bản xem trước ngay dưới
                    đổi chiều theo, nên thấy được trước khi in. */}
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={draft.labelPrinter.rotate}
                    disabled={!canEdit || !!busy}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        labelPrinter: { ...draft.labelPrinter, rotate: e.target.checked },
                      })
                    }
                  />
                  <span>
                    <span className="block font-medium text-ink">{t("settings.labelRotate")}</span>
                    <span className="block text-xs text-ink-soft">{t("settingsWorkspace.labelRotateHint")}</span>
                  </span>
                </label>
                <p className="text-xs text-ink-soft">
                  {t("settingsWorkspace.labelHint")}
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={"invalid" in labelSize}
                  onClick={async () => {
                    if ("invalid" in labelSize) return;
                    try {
                      printLabel(labelSize.size, testLabel);
                    } catch {
                      setNote(
                        "output",
                        t("settingsWorkspace.printError"),
                        true,
                      );
                    }
                  }}
                >
                  {t("settingsWorkspace.printTest")}
                </Button>
              </div>
              {"invalid" in labelSize ? (
                <p role="alert" className="text-danger">
                  {t("settingsWorkspace.labelHint")}
                </p>
              ) : (
                <LabelPreview size={labelSize.size} content={testLabel} />
              )}
            </div>
            {footer("output")}
          </form>
        )}
      </div>
      {tab !== "system" && (
        <>
          <p className="mt-3 text-xs text-ink-faint">
            {t("settings.lastUpdated", {
              when: base.updatedAt
                ? new Date(base.updatedAt).toLocaleString(i18n.language)
                : "—",
              who: base.updatedByName ?? "—",
            })}
          </p>
          <ConfigurationHistory resource="settings" enabled={canEdit} />
        </>
      )}
      {blocker.state === "blocked" && (
        <Modal
          title={t("settingsWorkspace.unsaved")}
          closeLabel={t("common.cancel")}
          onClose={() => blocker.reset()}
          className="max-w-md"
        >
          <div className="space-y-4 p-4">
            <p>{t("settingsWorkspace.leaveHint")}</p>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => blocker.reset()}>
                {t("settingsWorkspace.stay")}
              </Button>
              <Button
                variant="danger"
                disabled={!!busy}
                onClick={() => blocker.proceed()}
              >
                {t("settingsWorkspace.leave")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </PageBody>
  );
}
